import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import Cookies from 'js-cookie';
import { useDisconnect } from 'wagmi';

import { useRegisterUserVoteMutation } from '@query';

import { pingUserAction, RequestSignResult } from '@api';

import { useConnectAndSignModal } from '@shared/common/providers/GlobalModalsProvider';
import { namedWeb3Errors, storageKeys } from '@shared/constants';
import displayErrorToast, { normalizeError } from '@shared/helpers/displayErrorToast';
import getWalletActionStyles from '@shared/helpers/web3/getWalletActionStyles';
import getWalletTypeFlags from '@shared/helpers/web3/getWalletTypeFlags';
import useWeb3, { RequestSignProps } from '@shared/helpers/web3/hooks/useWeb3';
import useMagicIdUserWalletInfo from '@shared/hooks/useMagicIdUserWalletInfo';

import { storeSnapshotVote } from '@store:web/utils/hooks/useSnapshotStoredVote';

import { useSnapshotCastVoteMutation } from '../useSnapshotCastVoteMutation';

const VOTE_STRATEGY = {
  SNAPSHOT: 'SNAPSHOT',
  MAGICSTORE: 'MAGICSTORE',
} as const;

type VoteStrategyName = keyof typeof VOTE_STRATEGY;

export type VoteStrategyValue = (typeof VOTE_STRATEGY)[VoteStrategyName];

export const VOTE_CHOICE = {
  YES: 1,
  NO: 2,
} as const;

type VoteChoiceName = keyof typeof VOTE_CHOICE;

export type VoteChoiceValue = (typeof VOTE_CHOICE)[VoteChoiceName];

interface VoteStrategyParams {
  validationId: string;
  appId: number;
  appLink: string;
  appName: string;
  choice: VoteChoiceValue;
  reason?: string;
}

interface VoteStrategyReturn {
  isVoting: boolean;
  vote: (voteParams: VoteStrategyParams, params: { onSuccess: () => void }) => Promise<void>;
}

type UseVoteStrategy = () => VoteStrategyReturn;

const useSnapshotVote: UseVoteStrategy = () => {
  const { isPending: isLoading, mutateAsync: snapshotCastVote } = useSnapshotCastVoteMutation();
  const { disconnectAsync } = useDisconnect();
  const vote: VoteStrategyReturn['vote'] = useCallback(
    async ({ validationId, appLink: appId, choice, reason }, { onSuccess }) => {
      if (!choice) {
        throw new Error('Choice cannot be empty');
      }

      try {
        await snapshotCastVote({
          proposalId: validationId,
          appId,
          choice,
          reason,
        });

        onSuccess();

        // Note: needed to make our BE know about new votes
        await pingUserAction({
          json: {
            data: ['VALIDATION_VOTE_STORE', `${appId}`, choice, validationId],
          },
        });
      } catch (error) {
        const normalizedError = normalizeError(error);

        if (normalizedError.message === 'Account not provided') {
          await disconnectAsync();
        }

        throw normalizedError;
      }
    },
    [disconnectAsync, snapshotCastVote],
  );

  return {
    vote,
    isVoting: isLoading,
  };
};

const useMagicStoreVote: UseVoteStrategy = () => {
  const [isWalletLoading, setIsWalletLoading] = useState(false);
  const { isPending: isLoading, mutateAsync: registerUserVote } = useRegisterUserVoteMutation();
  const { disconnectAsync } = useDisconnect();
  const { requestSign } = useWeb3();
  const { getUserWalletType } = useMagicIdUserWalletInfo();
  const connectAndSignModal = useConnectAndSignModal();

  const getSignature = useCallback(
    async (signProps: RequestSignProps) => {
      Cookies.set(storageKeys.PREVENT_LOG_OUT_ON_ACCOUNT_CHANGE, 'true');

      const signResults = await requestSign(signProps);

      Cookies.remove(storageKeys.PREVENT_LOG_OUT_ON_ACCOUNT_CHANGE);

      return signResults;
    },
    [requestSign],
  );

  const vote: VoteStrategyReturn['vote'] = useCallback(
    async (voteParams, { onSuccess }) => {
      const { choice, validationId, appLink, reason, appId } = voteParams;

      if (!choice) {
        throw new Error('Choice cannot be empty');
      }

      const toastId = new Date().getTime();

      const handleSignResult = async (result?: RequestSignResult) => {
        if (!result) {
          return;
        }

        try {
          const { address, network, message, signature, v2 } = result;

          await registerUserVote({
            json: {
              appLink,
              vote: choice,
              appId,
              proposal: validationId,
              pubKey: address,
              network,
              message,
              signature,
              reason,
            },
            ...(v2
              ? {
                  headers: {
                    'x-sign-in': 'v2',
                  },
                }
              : {}),
          });

          onSuccess();

          storeSnapshotVote({
            appId: appLink,
            proposalId: validationId,
            account: address,
            choice,
          });
        } catch (error) {
          const normalizedError: Error = normalizeError(error);

          if (normalizedError.message === namedWeb3Errors.SIGNATURE_VERIFICATION_HAS_FAILED) {
            throw normalizedError;
          }

          displayErrorToast({ error });
        }
      };

      try {
        setIsWalletLoading(true);
        const { walletType, userWallet } = getUserWalletType();
        const { isWalletConnect } = getWalletTypeFlags(walletType);

        const message = `Vote (${voteParams.choice === 1 ? 'yes' : 'no'}) for ${
          voteParams.appName
        }`;

        const signProps: RequestSignProps = {
          walletType,
          message: `${message} at Magic Store`,
          targetAddress: userWallet.attributes.address,
          signType: 'SIWE',
        };

        if (isWalletConnect) {
          connectAndSignModal.open(handleSignResult, {
            signProps,
            confirmStepTitle: message,
            connectStepTitle: 'Please connect your wallet to Magic Store',
            confirmButtonText: 'Vote',
          });
        } else {
          const style = getWalletActionStyles(walletType);
          toast.loading('Loading...', { toastId, style });

          const results = await getSignature(signProps);
          await handleSignResult(results);
        }
      } catch (error) {
        const normalizedError = normalizeError(error);

        if (normalizedError.message === 'Account not provided') {
          await disconnectAsync();
        }

        throw normalizedError;
      } finally {
        // timeout for show smooth ui and don't blick if going api call after this.
        setTimeout(() => {
          setIsWalletLoading(false);
        }, 2000);
        toast.dismiss(toastId);
      }
    },
    [disconnectAsync, getSignature, registerUserVote, getUserWalletType, setIsWalletLoading],
  );

  return {
    vote,
    isVoting: isLoading || isWalletLoading,
  };
};

const useVoteStrategy = (strategy: VoteStrategyValue): VoteStrategyReturn => {
  const magicStore = useMagicStoreVote();
  const snapshot = useSnapshotVote();

  return strategy === VOTE_STRATEGY.MAGICSTORE ? magicStore : snapshot;
};

export default useVoteStrategy;
